import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Car, Bus, Siren, Truck, Battery, Bluetooth, Signal, 
  MapPin, Settings, Activity, Navigation, AlertTriangle, 
  Send, X, Search, Filter, ChevronLeft, ChevronRight 
} from 'lucide-react';
import './OBUManagement.css';

const OBUManagement = () => {
  const location = useLocation();
  
  // --- States สำหรับข้อมูล ---
  const [obsList] = useState([
    { id: 'OBU-TH-881', name: 'City Bus Line 140', role: 'BUS', battery: 85, status: 'active', location: { lat: 13.7600, lon: 100.5500 }, connections: { bluetooth: true, gps: true, gsm: '4G' } },
    { id: 'OBU-TH-999', name: 'Ambulance Unit A1', role: 'EMERGENCY', battery: 100, status: 'active', location: { lat: 13.7900, lon: 100.6000 }, connections: { bluetooth: true, gps: true, gsm: '5G' } },
    { id: 'OBU-TH-002', name: 'Maintenance Truck', role: 'SPECIAL', battery: 45, status: 'inactive', location: { lat: 13.7200, lon: 100.5200 }, connections: { bluetooth: false, gps: false, gsm: 'No Signal' } },
    { id: 'OBU-TH-105', name: 'Private Sedan 05', role: 'CAR', battery: 92, status: 'active', location: { lat: 13.7700, lon: 100.5800 }, connections: { bluetooth: true, gps: true, gsm: '5G' } },
    { id: 'OBU-TH-202', name: 'Logistic Truck B2', role: 'SPECIAL', battery: 60, status: 'active', location: { lat: 13.7500, lon: 100.5400 }, connections: { bluetooth: true, gps: true, gsm: '4G' } },
  ]);

  // --- States สำหรับ UI Control ---
  const [expandedId, setExpandedId] = useState(null);
  const [affectedVehicles, setAffectedVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // รับข้อมูลจากหน้าแผนที่
  const eventData = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      id: query.get('id'),
      lat: parseFloat(query.get('lat')),
      lon: parseFloat(query.get('lon')),
      detail: query.get('detail'),
      rsu: query.get('rsu'),
      dist: query.get('dist'),
      time: query.get('time')
    };
  }, [location.search]);

  // คำนวณรถที่ได้รับผลกระทบจากพิกัดเหตุการณ์
  useEffect(() => {
    if (eventData.lat && eventData.lon) {
      const calculateDist = (lat1, lon1, lat2, lon2) => {
        return Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lon1 - lon2, 2));
      };
      const nearby = obsList.filter(obs => {
        const dist = calculateDist(eventData.lat, eventData.lon, obs.location.lat, obs.location.lon);
        return dist < 0.05; 
      }).map(obs => obs.id);
      setAffectedVehicles(nearby);
      if (nearby.length > 0) setExpandedId(nearby[0]);
    }
  }, [eventData, obsList]);

  // --- Filter Logic ---
  const filteredList = useMemo(() => {
    return obsList.filter(obs => {
      const matchSearch = obs.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          obs.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchRole = filterRole === 'All' || obs.role === filterRole;
      return matchSearch && matchRole;
    });
  }, [obsList, searchTerm, filterRole]);

  // เมื่อ Filter เปลี่ยน ให้กลับไปหน้า 1
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterRole]);

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);

  const clearEvent = () => {
    if (eventData.id) {
      const savedEvents = JSON.parse(localStorage.getItem('MAP_EVENTS') || '[]');
      const filteredEvents = savedEvents.filter(e => e.id !== eventData.id);
      localStorage.setItem('MAP_EVENTS', JSON.stringify(filteredEvents));
      window.dispatchEvent(new Event('storage')); 
    }
    window.history.replaceState({}, '', window.location.pathname);
    setAffectedVehicles([]);
    setExpandedId(null);
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'BUS': return <Bus size={24} color="#2563eb" />;
      case 'EMERGENCY': return <Siren size={24} color="#dc2626" />;
      case 'SPECIAL': return <Truck size={24} color="#d97706" />;
      default: return <Car size={24} color="#4b5563" />;
    }
  };

  return (
    <div className="obs-container">
      {/* Event Alert Banner */}
      {eventData.id && (
        <div className="obu-event-banner fade-in">
          <div className="banner-left">
            <AlertTriangle className="blink-icon" color="#ff4d4d" size={28} />
            <div>
              <h3>กำลังตรวจสอบ OBU ที่ได้รับผลกระทบ: {eventData.detail}</h3>
              <p>สถานี {eventData.rsu} | พิกัด {eventData.lat}, {eventData.lon} | เวลา {eventData.time}</p>
            </div>
          </div>
          <button className="btn-close-banner" onClick={clearEvent}>
            <X size={20} /> ยุติการแจ้งเตือนและลบหมุดบนแผนที่
          </button>
        </div>
      )}

      <div className="obs-header">
        <div>
          <h2>OBU Management</h2>
          <p className="subtitle">รายการยานพาหนะติดตั้ง On-Board Unit</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <Search size={18} />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อหรือ ID รถ..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-select">
            <Filter size={18} />
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="All">ทุกประเภท</option>
              <option value="BUS">Bus</option>
              <option value="EMERGENCY">Emergency</option>
              <option value="SPECIAL">Truck/Special</option>
              <option value="CAR">Private Car</option>
            </select>
          </div>
        </div>
      </div>

      <div className="obs-list">
        {currentItems.length > 0 ? currentItems.map((obs) => {
          const isAffected = affectedVehicles.includes(obs.id);
          return (
            <div key={obs.id} className={`obs-card ${expandedId === obs.id ? 'expanded' : ''} ${isAffected ? 'affected-border' : ''}`}>
              <div className="obs-row-main">
                <div className="col-icon">
                  <div className={`icon-box role-${obs.role.toLowerCase()}`}>{getRoleIcon(obs.role)}</div>
                </div>
                <div className="col-info">
                  <h4>{obs.name} {isAffected && <span className="affected-tag">⚠️ พบเหตุใกล้เคียง</span>}</h4>
                  <div className="badges">
                    <span className="id-badge">{obs.id}</span>
                    <span className={`role-badge role-${obs.role.toLowerCase()}`}>{obs.role}</span>
                  </div>
                </div>
                <div className="col-status">
                  <div className="status-item"><Battery size={16} color={obs.battery < 20 ? '#ef4444' : '#10b981'} /><span>{obs.battery}%</span></div>
                  <div className="status-item"><Signal size={16} color={obs.connections.gsm !== 'No Signal' ? '#2563eb' : '#9ca3af'} /><span>{obs.connections.gsm}</span></div>
                </div>
                <div className="col-actions">
                  <button className="btn-icon-settings" onClick={() => setExpandedId(expandedId === obs.id ? null : obs.id)}>
                    <Settings size={18} /><span>{expandedId === obs.id ? 'ปิด' : 'รายละเอียด'}</span>
                  </button>
                </div>
              </div>

              {expandedId === obs.id && (
                <div className="obs-config-panel fade-in">
                  <div className="config-grid">
                    <div className="config-section">
                      <h5><Activity size={14}/> Connectivity Status</h5>
                      <div className="status-pills-row">
                        <div className={`pill ${obs.connections.bluetooth ? 'on' : ''}`}>Bluetooth</div>
                        <div className={`pill ${obs.connections.gps ? 'on' : ''}`}>GPS Fixed</div>
                        <div className="pill on">{obs.connections.gsm}</div>
                      </div>
                    </div>
                    <div className="config-section">
                      <h5><Navigation size={14}/> V2X Actions</h5>
                      {isAffected ? (
                        <div className="v2x-alert-action">
                          <p>⚠️ ตรวจพบ {eventData.detail} ในเส้นทาง</p>
                          <button className="btn-broadcast"><Send size={14} /> ส่งคำเตือนไปยังหน้าจอรถยนต์</button>
                        </div>
                      ) : (
                        <div className="sim-control">
                          <label>ความเร็วจำลอง (km/h)</label>
                          <input type="number" defaultValue={obs.status === 'active' ? 60 : 0} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }) : (
          <div className="no-results">ไม่พบข้อมูล OBU ที่ค้นหา</div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-bar">
          <button 
            disabled={currentPage === 1} 
            onClick={() => setCurrentPage(prev => prev - 1)}
            className="page-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="page-info">หน้า {currentPage} จาก {totalPages}</span>
          <button 
            disabled={currentPage === totalPages} 
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="page-btn"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default OBUManagement;